import type { BoardSummary } from '@doriavboard/shared';
import { Lock, SearchX } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router';
import { ApiError, errorMessage } from '../api/client';
import { boardsApi } from '../api/endpoints';
import { CenteredMessage } from '../components/CenteredMessage';
import { ContinueAsGuest } from '../components/ContinueAsGuest';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Logo } from '../components/Logo';
import { FullPageSpinner } from '../components/ui/Spinner';
import { useSession } from '../stores/session';

/** Who is editing: an account, or a guest known only by name. */
export interface BoardIdentity {
  id: string;
  name: string;
  color: string;
  isGuest: boolean;
}

// The editor (canvas, tools, sync) is the heavy part of the app; load it on demand.
const BoardWorkspace = lazy(() => import('../board/BoardWorkspace'));

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; board: BoardSummary }
  | { status: 'error'; code: number; message: string };

/**
 * Someone without a session opened a shared link: they join as a guest under
 * a name (which also gives them a dashboard), or sign in.
 */
function GuestJoin({ board, next }: { board: BoardSummary; next: string }) {
  const allowGuests = useSession((s) => s.config?.allowGuests !== false);
  return (
    <div className="dotted-backdrop flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-float dark:border-slate-800 dark:bg-slate-900">
        <Logo className="mb-6" />
        <h1 className="text-xl font-semibold">Join “{board.title}”</h1>
        <p className="mt-1 mb-5 text-sm text-slate-500">
          {board.owner.name} shared this board.{' '}
          {allowGuests ? 'Tell the others who you are.' : 'Sign in to join.'}
        </p>
        {/* Signing in as a guest reloads the board for the new user. */}
        <ContinueAsGuest startOpen onDone={() => {}} />
        <p className="mt-5 text-center text-sm text-slate-500">
          Have an account?{' '}
          <Link
            to={`/login?next=${encodeURIComponent(next)}`}
            className="font-medium text-brand-600 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export function BoardPage() {
  const { boardId = '' } = useParams();
  const location = useLocation();
  const user = useSession((s) => s.user);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const userId = user?.id;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the signed-in user changes, since their role may differ
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    boardsApi
      .get(boardId)
      .then(({ board }) => {
        if (!cancelled) setState({ status: 'ready', board });
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err instanceof ApiError ? err.status : 0;
        setState({ status: 'error', code, message: errorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [boardId, userId]);

  /**
   * Looks the board up again while it stays open, when the server signals
   * that this person's access changed: a new role reopens the board with it,
   * and lost access shows why the board can't be opened any more.
   */
  const refresh = useCallback(() => {
    boardsApi
      .get(boardId)
      .then(({ board }) =>
        setState((current) => (current.status === 'ready' ? { status: 'ready', board } : current)),
      )
      .catch((err) => {
        // Offline or a server hiccup: the board on screen is still the best there is.
        if (!(err instanceof ApiError) || err.status === 0 || err.status >= 500) return;
        setState({ status: 'error', code: err.status, message: errorMessage(err) });
      });
  }, [boardId]);

  if (state.status === 'loading') return <FullPageSpinner label="Opening board…" />;

  const here = location.pathname;
  if (state.status === 'error') {
    if (state.code === 401)
      return <Navigate to={`/login?next=${encodeURIComponent(here)}`} replace />;
    if (state.code === 403) {
      return (
        <CenteredMessage icon={<Lock className="size-6" />} title="You need access">
          <p>
            Ask the owner to share this board with{' '}
            {user?.username ? <strong>{user.username}</strong> : 'you'}.
          </p>
          <p className="mt-4">
            <Link to="/" className="font-medium text-brand-600 hover:underline">
              Back to your boards
            </Link>
          </p>
        </CenteredMessage>
      );
    }
    return (
      <CenteredMessage
        icon={<SearchX className="size-6" />}
        title={state.code === 404 ? 'Board not found' : 'Couldn’t open the board'}
      >
        <p>
          {state.code === 404 ? 'It may have been deleted, or the link is wrong.' : state.message}
        </p>
        <p className="mt-4">
          <Link to="/" className="font-medium text-brand-600 hover:underline">
            Back to your boards
          </Link>
        </p>
      </CenteredMessage>
    );
  }

  if (!user) return <GuestJoin board={state.board} next={here} />;
  const identity: BoardIdentity = {
    id: user.id,
    name: user.name,
    color: user.color,
    isGuest: user.isGuest,
  };

  return (
    <ErrorBoundary what="this board">
      <Suspense fallback={<FullPageSpinner label="Opening board…" />}>
        <BoardWorkspace
          key={state.board.id}
          board={state.board}
          identity={identity}
          onBoardChange={(board) => setState({ status: 'ready', board })}
          onAccessChange={refresh}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
